/**
 * Sprint T-18 · Notification Studio · Legacy Migration Dashboard.
 *
 * Inventaria call-sites de `send_email_safe(...)` + counters
 * legacy_dynamic vs modern deliveries · guía para el sprint T-19 (cutover final).
 */
import React, { useState } from "react";
import { Loader2, Code2, TrendingDown, TrendingUp, ChevronDown, ChevronRight } from "lucide-react";
import { useLegacySummary, useLegacyCallsites } from "../../hooks/useNotifications";

function BigNum({ label, value, subtitle, tone = "neutral", testid }) {
  const cls =
    tone === "warn"
      ? "text-amber-300"
      : tone === "success"
      ? "text-emerald-300"
      : "text-[var(--ag-text)]";
  return (
    <div
      className="rounded border border-[var(--ag-border)] bg-[var(--ag-panel)] p-4"
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest text-[var(--ag-text-dim)]">
        {label}
      </div>
      <div className={`font-display text-4xl font-bold tracking-tight ${cls}`}>
        {value ?? "—"}
      </div>
      {subtitle && (
        <div className="mt-1 text-[10px] text-[var(--ag-text-dim)]">{subtitle}</div>
      )}
    </div>
  );
}

function ProgressBar({ pctLegacy = 0, pctModern = 0 }) {
  return (
    <div
      className="rounded border border-[var(--ag-border)] bg-[var(--ag-panel)] p-3"
      data-testid="studio-legacy-progress-bars"
    >
      <div className="mb-2 flex items-center justify-between text-[10px] text-[var(--ag-text-dim)]">
        <span className="flex items-center gap-1 text-amber-300">
          <TrendingDown size={11} /> Legacy · {pctLegacy}%
        </span>
        <span className="flex items-center gap-1 text-emerald-300">
          <TrendingUp size={11} /> Moderno · {pctModern}%
        </span>
      </div>
      <div className="h-3 w-full overflow-hidden rounded bg-[var(--ag-border)]">
        <div
          className="h-full bg-emerald-500"
          style={{ width: `${pctModern}%` }}
          data-testid="studio-legacy-progress-modern"
        />
      </div>
    </div>
  );
}

function CallsiteRow({ site }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded border border-[var(--ag-border)]/70 bg-[var(--ag-panel)]"
      data-testid={`studio-legacy-callsite-row-${site.file.replace(/[^a-zA-Z0-9]/g, "-")}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[11px] hover:bg-black/20"
      >
        {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        <Code2 size={11} className="text-[var(--ag-text-dim)]" />
        <span className="flex-1 font-mono text-xs text-[var(--ag-text)]">{site.file}</span>
        <span className="rounded bg-amber-500/20 px-2 py-0.5 font-mono text-[10px] text-amber-200">
          {site.usage_count} uso{site.usage_count > 1 ? "s" : ""}
        </span>
      </button>
      {open && (
        <ul className="border-t border-[var(--ag-border)]/50 px-3 py-2 text-[10px]">
          {site.occurrences.map((occ, i) => (
            <li key={i} className="py-0.5 font-mono">
              <span className="text-[var(--ag-text-dim)]">L{occ.line}</span>{" "}
              <span className="text-emerald-300">{occ.function}</span>{" "}
              <span className="text-[var(--ag-text-dim)]">→</span>{" "}
              <span className="text-[var(--ag-text)]">{occ.snippet}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function LegacyMigrationTab() {
  const [days, setDays] = useState(30);
  const summaryQ = useLegacySummary({ days });
  const csQ = useLegacyCallsites();
  const summary = summaryQ.data;
  const cs = csQ.data;

  return (
    <div className="space-y-4" data-testid="studio-legacy-tab">
      {/* Header */}
      <div className="rounded border border-amber-500/40 bg-amber-500/5 p-3">
        <div className="text-[10px] uppercase tracking-widest text-amber-300">
          Objetivo T-19 · Cutover final
        </div>
        <p className="mt-1 text-xs text-[var(--ag-text)]">
          Eliminar completamente el wrapper{" "}
          <span className="rounded bg-black/40 px-1 py-0.5 font-mono text-amber-200">
            email.legacy_dynamic.v1
          </span>{" "}
          y migrar los call-sites detectados a templates modernos tipados.
        </p>
      </div>

      {/* Range selector */}
      <div className="flex items-center gap-2 text-[11px]">
        <span className="text-[var(--ag-text-dim)]">Rango deliveries:</span>
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            data-testid={`studio-legacy-range-${d}d-btn`}
            className={`rounded border px-2 py-1 ${
              days === d
                ? "border-[var(--ag-gold)] bg-[var(--ag-gold)]/10 text-[var(--ag-text)]"
                : "border-[var(--ag-border)] text-[var(--ag-text-dim)]"
            }`}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* Big numbers */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <BigNum
          label="Deliveries legacy"
          value={summaryQ.isLoading ? "…" : summary?.legacy_count ?? 0}
          subtitle={`en ${days}d · ${summary?.legacy_pct ?? 0}% del total`}
          tone="warn"
          testid="studio-legacy-bignum-legacy-count"
        />
        <BigNum
          label="Call-sites detectados"
          value={csQ.isLoading ? "…" : cs?.total_callsites ?? 0}
          subtitle={cs?.files_touched ? `en ${cs.files_touched} ficheros` : "análisis estático"}
          tone={cs?.total_callsites ? "warn" : "success"}
          testid="studio-legacy-bignum-callsites"
        />
        <BigNum
          label="Deliveries modernas"
          value={summaryQ.isLoading ? "…" : summary?.modern_count ?? 0}
          subtitle={`${summary?.modern_pct ?? 0}% del total`}
          tone="success"
          testid="studio-legacy-bignum-modern-count"
        />
      </div>

      <ProgressBar
        pctLegacy={summary?.legacy_pct ?? 0}
        pctModern={summary?.modern_pct ?? 0}
      />

      {/* Call-sites list */}
      <div className="space-y-2" data-testid="studio-legacy-callsites-list">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ag-text-dim)]">
            Call-sites de `send_email_safe(...)` · cache 5min
          </div>
          {cs?.cache_hit && (
            <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[9px] text-emerald-300">
              cache hit · {cs.cache_age_seconds}s
            </span>
          )}
        </div>
        {csQ.isLoading && (
          <div className="flex items-center gap-2 text-xs text-[var(--ag-text-dim)]">
            <Loader2 size={12} className="animate-spin" /> Escaneando…
          </div>
        )}
        {cs?.callsites?.length === 0 && (
          <div
            className="rounded border border-dashed border-[var(--ag-border)] p-4 text-center text-xs text-emerald-300"
            data-testid="studio-legacy-callsites-empty"
          >
            🎉 No hay call-sites de `send_email_safe(...)` en el código actual.
          </div>
        )}
        {cs?.callsites?.map((site) => (
          <CallsiteRow key={site.file} site={site} />
        ))}
      </div>

      {/* Distribution by template */}
      {summary?.by_template && (
        <div
          className="rounded border border-[var(--ag-border)] bg-[var(--ag-panel)] p-3"
          data-testid="studio-legacy-by-template"
        >
          <div className="mb-2 text-[10px] uppercase tracking-widest text-[var(--ag-text-dim)]">
            Distribución por template (últimos {days}d)
          </div>
          <table className="w-full text-[11px]">
            <thead className="text-[9px] uppercase tracking-widest text-[var(--ag-text-dim)]">
              <tr>
                <th className="py-1 text-left">template_id</th>
                <th className="py-1 text-right">count</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(summary.by_template)
                .sort((a, b) => b[1] - a[1])
                .map(([tid, count]) => (
                  <tr
                    key={tid}
                    className="border-t border-[var(--ag-border)]/50"
                    data-testid={`studio-legacy-tpl-row-${(tid || "").slice(0, 40)}`}
                  >
                    <td className="py-1 font-mono text-xs">
                      {tid === "email.legacy_dynamic.v1" ? (
                        <span className="text-amber-300">{tid} ⚠</span>
                      ) : (
                        tid
                      )}
                    </td>
                    <td className="py-1 text-right font-mono">{count}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
